import { createAction, createAsyncThunk, createReducer } from '@reduxjs/toolkit'
import axios from 'axios'

const initialState = {
  cart: null,
  brands: [],
  products: {},
  colourSwatches: {},
  featuredCategories: null,
  sizeMap: null,
  sizingMode: 'gb',
  rangeDetails: {},
  recentProducts: null,
  doofinderLoaded: false
}

export const setBrands = createAction('SET_BRANDS')
export const setSeasons = createAction('SET_SEASONS')
export const setProducts = createAction('SET_PRODUCTS')
export const updateCart = createAction('UPDATE_CART')
export const setColourSwatches = createAction('SET_COLOUR_SWATCHES')
export const setFeaturedCategories = createAction('SET_FEATURED_CATEGORIES')
export const setSizeMap = createAction('SET_SIZE_MAP')
export const setSizingMode = createAction('SET_SIZING_MODE')
export const setRangeDetails = createAction('SET_RANGE_DETAILS')
export const setRecentProducts = createAction('SET_RECENT_PRODUCTS')
export const clearRecentProducts = createAction('CLEAR_RECENT_PRODUCTS')
export const setDoofinderLoaded = createAction('SET_DOOFINDER_LOADED')

export default createReducer(initialState, builder => {
  builder
    .addCase(setBrands, (state, action) => {
      state.brands = action.payload
    })
    .addCase(setProducts, (state, action) => {
      state.products[action.payload.range] = action.payload.products
    })
    .addCase(updateCart, (state, action) => {
      state.cart = action.payload || null
    })
    .addCase(setFeaturedCategories, (state, action) => {
      state.featuredCategories = action.payload
    })
    .addCase(setSizeMap, (state, action) => {
      state.sizeMap = action.payload
    })
    .addCase(setSizingMode, (state, action) => {
      state.sizingMode = action.payload
    })
    .addCase(setRangeDetails, (state, action) => {
      state.rangeDetails[action.payload.range] = action.payload.data
    })
    .addCase(setRecentProducts, (state, action) => {
      state.recentProducts = action.payload
    })
    .addCase(clearRecentProducts, state => {
      state.recentProducts = null
    })
    .addCase(setDoofinderLoaded, (state, action) => {
      state.doofinderLoaded = action.payload
    })
    .addCase(setColourSwatches, (state, action) => {
      action.payload.forEach(swatch => {
        state.colourSwatches[swatch.colour_code] = swatch
      })
    })
})

export const loadSize = createAsyncThunk(
  'shop/loadSize',
  async function (data = {}, { dispatch, getState }) {
    const response = await axios.get('/api/config/default-size')

    dispatch(setSizingMode(response.data))
  }
)

export const loadBrands = createAsyncThunk(
  'shop/loadBrands',
  async function (data = {}, { dispatch, getState }) {
    if (getState().shop.brands.length > 0) {
      return
    }

    const response = await axios.get('/api/brands')

    dispatch(setBrands(response.data))
  }
)

export const loadCart = createAsyncThunk(
  'shop/loadCart',
  async function (data = {}, { dispatch, getState }) {
    const response = await axios.get('/api/cart')

    dispatch(updateCart(response.data))
  }
)

export const loadFeaturedCategories = createAsyncThunk(
  'shop/loadFeaturedCategories',
  async function (data = {}, { dispatch, getState }) {
    if (getState().shop.featuredCategories) {
      return
    }

    const response = await axios.get('/api/categories/featured')

    dispatch(setFeaturedCategories(response.data))
  }
)

export const loadRecentProducts = createAsyncThunk(
  'shop/loadRecentProducts',
  async function (data = {}, { dispatch, getState }) {
    if (getState().shop.recentProducts) {
      return
    }

    const response = await axios.get('/api/products/recent', {
      params: {
        products: getState().recentProducts
      }
    })

    dispatch(setRecentProducts(response.data.data || []))
    dispatch(setColourSwatches(response.data.colourSwatches || []))
  }
)

export const loadSizeMap = createAsyncThunk(
  'shop/loadSizeMap',
  async function (data = {}, { dispatch, getState }) {
    if (getState().shop.sizeMap) {
      return
    }

    const response = await axios.get('/api/config/sizes')

    dispatch(setSizeMap(response.data))
  }
)

export const loadRangeProducts = createAsyncThunk(
  'shop/loadRangeProducts',
  function (range, { dispatch, getState }) {
    return new Promise((resolve, reject) => {
      if (getState().shop.products[range]) {
        return resolve()
      }

      axios.get(`/api/ranges/${range}/products`)
        .then(response => {
          dispatch(setProducts({
            range,
            products: response.data.data
          }))

          dispatch(setColourSwatches(response.data.colourSwatches))

          dispatch(setRangeDetails({
            range,
            data: response.data.range
          }))

          resolve()
        })
        .catch(e => {
          reject(e)
        })
    })
  }
)
